/**
 * Flow Optimizer ranking tags — shared contract.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A SECOND tag system (separate from performance tags) that tags paths/landers/
 * offers by the optimizer's RAW SCORE ranking, optionally AND-combined with
 * normal metric conditions. Used by both the backend (stats endpoint) and the
 * frontend (Flow Optimizer preview) through one pure evaluator.
 */

import type { FlowLayer, FlowOptimizerItemPreview } from "../voluum-flow-optimizer.types";
import { LIST_TAG_RULE_FIELDS } from "../list-tag-rule-fields";
import type { TagCompareTo } from "../flow-optimizer-compare-to";
import type {
  TagCampaignThreshold,
  TagCountryThreshold,
} from "../tag-rules/override-context";

/** Numeric comparison operators — same set the performance tags use. */
export type TagComparisonOperator = "gte" | "lte" | "gt" | "lt" | "eq";

/** Metric fields a flow-tag metric condition can test (reuses the perf-tag set). */
export const FLOW_TAG_METRIC_FIELDS = LIST_TAG_RULE_FIELDS;

/** Optimizer values a ranking condition can rank by. */
export const FLOW_TAG_RANKING_METRICS = [
  "rawScore",
  "scoreShare",
  "optimalWeight",
] as const;
export type FlowTagRankingMetric = (typeof FLOW_TAG_RANKING_METRICS)[number];

/** Ranking operators (the new, optimizer-only operators). */
export const FLOW_TAG_RANKING_OPS = [
  "highest",
  "lowest",
  "top_percent",
  "bottom_percent",
] as const;
export type FlowTagRankingOp = (typeof FLOW_TAG_RANKING_OPS)[number];

/** Pool a ranking condition ranks within (paths are always flow-wide). */
export type FlowTagRankingScope = "path" | "flow";

/** @deprecated Alias of the shared {@link TagCountryThreshold}; kept so existing
 *  imports keep working. New code should use the shared type. */
export type FlowTagCountryThreshold = TagCountryThreshold;

/** Rank this item by an optimizer value against its competitors. */
export interface FlowTagRankingCondition {
  kind: "ranking";
  metric: FlowTagRankingMetric;
  op: FlowTagRankingOp;
  /** Required for top_percent / bottom_percent — an integer in [1, 100]. */
  percent?: number;
  /** Landers/offers only; ignored for paths (always flow). Default "path". */
  scope?: FlowTagRankingScope;
}

/** Normal metric threshold (mirrors a performance-tag condition 1:1).
 *  Overrides resolve campaign → country → `value`; see resolveOverrideThreshold. */
export interface FlowTagMetricCondition {
  kind: "metric";
  field: string;
  operator: TagComparisonOperator;
  value: number;
  countryThresholds?: TagCountryThreshold[];
  /** Per-campaign thresholds — narrower than country, so they win. */
  campaignThresholds?: TagCampaignThreshold[];
  /**
   * When set, the threshold is another metric (× percent/100) over its own
   * window instead of `value` — which is then ignored, as are the override
   * lists. Missing RHS data ⇒ the condition is false. See
   * flow-optimizer-compare-to.ts.
   */
  compareTo?: TagCompareTo;
}

/** A rule's conditions are AND-combined. */
export type FlowTagCondition = FlowTagRankingCondition | FlowTagMetricCondition;

/** A flow-optimizer tag rule (one layer). Mirrors the perf-tag rule shape. */
/**
 * WHICH elements a rule is allowed to judge. A population, not a condition:
 * conditions are AND-combined and this one would AND with nothing, and
 * `!isRankingCondition(c)` means "metric" in six places that a third condition
 * kind would break silently.
 *
 * - `scored`   — the normal pool: active, not excluded, has stats. The default,
 *                and what every rule written before this feature is.
 * - `no-stats` — ONLY elements the tracker has no numbers for yet. These are
 *                deliberately kept out of scoring (a 0 would punish a brand-new
 *                element), which also kept them out of tagging until now.
 *
 * An enum, not a boolean: it cannot contradict itself, and it leaves room for
 * a future `low-volume` without another migration.
 */
export type FlowTagRuleAppliesTo = "scored" | "no-stats";

export interface FlowTagRule {
  id: string;
  name: string;
  color?: string | null;
  /** Optional emoji/icon shown with the tag (and alone in tight layouts). */
  emoji?: string | null;
  layer: FlowLayer;
  conditions: FlowTagCondition[];
  dateRangePreset?: string;
  fromDate?: string | null;
  toDate?: string | null;
  toDateIsToday?: boolean;
  enabled?: boolean;
  orderIndex?: number;
  groupKey?: string | null;
  /** Undefined is `"scored"` — every pre-existing rule, unchanged. */
  appliesTo?: FlowTagRuleAppliesTo;
}

/** The subset of a preview item the evaluator needs. */
export type FlowTagItem = Pick<
  FlowOptimizerItemPreview,
  | "layer"
  | "itemId"
  | "itemName"
  | "pathId"
  | "parentId"
  | "rawScore"
  | "scoreShare"
  | "optimalWeight"
  | "active"
  | "excluded"
  | "ruleExcluded"
  | "noMetrics"
  | "metricsByDateRange"
  | "breakdown"
>;

export interface FlowTagResult {
  /** key = `${layer}|${itemId}|${pathId ?? ''}` -> tag names (in order applied). */
  tagsByKey: Record<string, string[]>;
  /** tag name -> color (last writer wins). */
  tagColors: Record<string, string>;
  /** tag name -> emoji/icon (last writer wins). */
  tagEmojis: Record<string, string>;
}

export function flowTagItemKey(
  layer: FlowLayer,
  itemId: string,
  pathId?: string,
): string {
  return `${layer}|${itemId}|${pathId ?? ""}`;
}

export function isRankingCondition(
  c: FlowTagCondition,
): c is FlowTagRankingCondition {
  return c.kind === "ranking";
}
